<template>
  <div class="attendance-viewer" :style="themeStyle">
    <div class="d-flex flex-wrap align-center attendance-viewer__header">
      <div>
        <div class="attendance-viewer__eyebrow">Acompanhamento</div>
        <div class="text-h6 font-weight-bold">
          {{ title }}
        </div>
        <div v-if="description" class="attendance-viewer__description mt-1">
          {{ description }}
        </div>
      </div>

      <v-spacer />

      <v-btn
        text
        rounded
        :color="theme.primary"
        :loading="loading"
        @click="$emit('refresh')"
      >
        Atualizar presença
      </v-btn>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      rounded
      height="4"
      class="mt-4"
      :color="theme.primary"
    />

    <v-alert
      v-else-if="!monthOptions.length"
      type="info"
      text
      class="mb-0 mt-4"
    >
      O professor ainda não registrou presenças para esta turma.
    </v-alert>

    <template v-else>
      <v-row dense class="mt-2">
        <v-col cols="12" md="6">
          <v-select
            :value="selectedMonthKey"
            :items="monthOptions"
            item-text="text"
            item-value="value"
            label="Mês de referência"
            outlined
            dense
            hide-details="auto"
            :disabled="loading || !monthOptions.length"
            @change="$emit('change-month', $event)"
          />
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="d-flex flex-wrap justify-end align-center"
        >
          <v-chip class="mr-2 mb-2" small dark :color="theme.primary">
            {{ safeSummary.totalLessons }} aula{{
              safeSummary.totalLessons === 1 ? "" : "s"
            }}
          </v-chip>

          <v-chip
            v-if="safeSummary.futureCount"
            class="mr-2 mb-2"
            small
            outlined
            color="warning"
          >
            {{ safeSummary.futureCount }} futura{{
              safeSummary.futureCount === 1 ? "" : "s"
            }}
          </v-chip>

          <v-chip
            v-if="safeSummary.pendingCount"
            class="mb-2"
            small
            outlined
            :color="theme.secondary"
          >
            {{ safeSummary.pendingCount }} pendente{{
              safeSummary.pendingCount === 1 ? "" : "s"
            }}
          </v-chip>
        </v-col>
      </v-row>

      <div class="attendance-viewer__hint mt-4">
        Verde indica presença, vermelho indica falta e cinza representa aula sem
        registro fechado ainda.
      </div>

      <v-alert
        v-if="!loading && selectedMonthKey && !events.length"
        type="info"
        text
        class="mb-0 mt-4"
      >
        Nenhuma aula encontrada para o mês selecionado.
      </v-alert>

      <div v-else class="attendance-viewer__events mt-6">
        <div
          v-for="event in events"
          :key="event.id"
          class="attendance-viewer__event"
        >
          <div class="attendance-viewer__weekday">
            {{ formatAttendanceWeekday(event.eventDate) }}
          </div>

          <div
            class="attendance-viewer__circle"
            :class="attendanceCircleClass(event)"
          >
            {{ attendanceCircleLabel(event) }}
          </div>

          <div class="attendance-viewer__date">
            {{ formatAttendanceDayMonth(event.eventDate) }}
          </div>

          <div class="attendance-viewer__time">
            {{ event.startsAt }}
          </div>

          <div
            class="attendance-viewer__status"
            :class="attendanceStatusClass(event)"
          >
            {{ attendanceStatusLabel(event) }}
          </div>
        </div>
      </div>

      <div class="attendance-viewer__summary mt-8">
        <div class="attendance-viewer__donut" :style="attendanceDonutStyle">
          <div class="attendance-viewer__donut-inner">
            <div class="attendance-viewer__donut-value">
              {{ formatPercentage(safeSummary.presentPercentage) }}
            </div>
            <div class="attendance-viewer__donut-label">Frequência</div>
          </div>
        </div>

        <div class="attendance-viewer__stats">
          <div class="attendance-viewer__stat">
            <span class="attendance-viewer__stat-label">% de frequência</span>
            <strong class="attendance-viewer__stat-value text-success">
              {{ formatPercentage(safeSummary.presentPercentage) }}
            </strong>
          </div>

          <div class="attendance-viewer__stat">
            <span class="attendance-viewer__stat-label">% de faltas</span>
            <strong class="attendance-viewer__stat-value text-error">
              {{ formatPercentage(safeSummary.absentPercentage) }}
            </strong>
          </div>

          <div class="attendance-viewer__stat">
            <span class="attendance-viewer__stat-label"
              >Total de presenças</span
            >
            <strong class="attendance-viewer__stat-value">
              {{ safeSummary.presentCount }}
            </strong>
          </div>

          <div class="attendance-viewer__stat">
            <span class="attendance-viewer__stat-label">Total de faltas</span>
            <strong class="attendance-viewer__stat-value">
              {{ safeSummary.absentCount }}
            </strong>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "ClassAttendanceViewer",

  props: {
    theme: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    monthOptions: {
      type: Array,
      default: () => [],
    },
    selectedMonthKey: {
      type: String,
      default: null,
    },
    events: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: "Minha frequência",
    },
    description: {
      type: String,
      default: "",
    },
  },

  computed: {
    safeSummary() {
      return {
        totalLessons: +this.summary?.totalLessons || 0,
        presentCount: +this.summary?.presentCount || 0,
        absentCount: +this.summary?.absentCount || 0,
        pendingCount: +this.summary?.pendingCount || 0,
        futureCount: +this.summary?.futureCount || 0,
        presentPercentage: +this.summary?.presentPercentage || 0,
        absentPercentage: +this.summary?.absentPercentage || 0,
      };
    },
    attendanceDonutStyle() {
      if (!this.safeSummary.totalLessons) {
        return {
          background: "conic-gradient(rgba(148, 163, 184, 0.22) 0deg 360deg)",
        };
      }

      const presentAngle =
        (this.safeSummary.presentCount / this.safeSummary.totalLessons) * 360;
      const absentAngle =
        presentAngle +
        (this.safeSummary.absentCount / this.safeSummary.totalLessons) * 360;

      return {
        background: `conic-gradient(#16a34a 0deg ${presentAngle}deg, #ef4444 ${presentAngle}deg ${absentAngle}deg, rgba(148, 163, 184, 0.22) ${absentAngle}deg 360deg)`,
      };
    },
    themeStyle() {
      return {
        "--attendance-primary": this.theme.primary,
        "--attendance-secondary": this.theme.secondary,
      };
    },
  },

  methods: {
    formatAttendanceWeekday(value) {
      if (!value) {
        return "-";
      }

      const date = value instanceof Date ? value : new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat("pt-BR", {
        weekday: "short",
      })
        .format(date)
        .replace(".", "")
        .toUpperCase();
    },
    formatAttendanceDayMonth(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      }).format(date);
    },
    attendanceCircleLabel(event) {
      if (+event?.status === 1) {
        return "P";
      }

      if (+event?.status === 2) {
        return "F";
      }

      return new Date(event.eventDate).getDate().toString().padStart(2, "0");
    },
    attendanceCircleClass(event) {
      if (event?.isFuture) {
        return "attendance-viewer__circle--future";
      }

      if (+event?.status === 1) {
        return "attendance-viewer__circle--present";
      }

      if (+event?.status === 2) {
        return "attendance-viewer__circle--absent";
      }

      return "attendance-viewer__circle--pending";
    },
    attendanceStatusLabel(event) {
      if (event?.isFuture) {
        return "Futura";
      }

      if (+event?.status === 1) {
        return "Presente";
      }

      if (+event?.status === 2) {
        return "Falta";
      }

      return "Pendente";
    },
    attendanceStatusClass(event) {
      if (event?.isFuture) {
        return "attendance-viewer__status--future";
      }

      if (+event?.status === 1) {
        return "attendance-viewer__status--present";
      }

      if (+event?.status === 2) {
        return "attendance-viewer__status--absent";
      }

      return "attendance-viewer__status--pending";
    },
    formatPercentage(value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
.attendance-viewer {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.attendance-viewer__header {
  gap: 1rem;
}

.attendance-viewer__eyebrow {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--attendance-secondary);
  font-weight: 700;
}

.attendance-viewer__description {
  color: rgba(31, 41, 55, 0.72);
  max-width: 620px;
  line-height: 1.5;
}

.attendance-viewer__hint {
  color: rgba(31, 41, 55, 0.72);
  font-size: 0.95rem;
}

.attendance-viewer__events {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: 1rem;
}

.attendance-viewer__event {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.attendance-viewer__weekday {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(31, 41, 55, 0.72);
  margin-bottom: 0.45rem;
}

.attendance-viewer__circle {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 700;
}

.attendance-viewer__circle--pending {
  background: rgba(148, 163, 184, 0.18);
  color: #334155;
}

.attendance-viewer__circle--present {
  background: #16a34a;
  color: #ffffff;
  box-shadow: 0 14px 28px rgba(22, 163, 74, 0.22);
}

.attendance-viewer__circle--absent {
  background: #ef4444;
  color: #ffffff;
  box-shadow: 0 14px 28px rgba(239, 68, 68, 0.18);
}

.attendance-viewer__circle--future {
  background: rgba(245, 158, 11, 0.14);
  color: #92400e;
  box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.2);
}

.attendance-viewer__date {
  margin-top: 0.55rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2937;
}

.attendance-viewer__time {
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: rgba(31, 41, 55, 0.64);
}

.attendance-viewer__status {
  margin-top: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.attendance-viewer__status--present {
  color: #15803d;
}

.attendance-viewer__status--absent {
  color: #dc2626;
}

.attendance-viewer__status--pending {
  color: #64748b;
}

.attendance-viewer__status--future {
  color: #b45309;
}

.attendance-viewer__summary {
  display: grid;
  grid-template-columns: minmax(180px, 220px) 1fr;
  gap: 1.5rem;
  align-items: center;
}

.attendance-viewer__donut {
  width: 200px;
  height: 200px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.attendance-viewer__donut-inner {
  width: 122px;
  height: 122px;
  border-radius: 999px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.attendance-viewer__donut-value {
  font-size: 1.65rem;
  font-weight: 700;
  color: #111827;
}

.attendance-viewer__donut-label {
  margin-top: 0.2rem;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(31, 41, 55, 0.6);
}

.attendance-viewer__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.attendance-viewer__stat {
  border-radius: 18px;
  padding: 1rem 1.1rem;
  background: rgba(15, 23, 42, 0.04);
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.attendance-viewer__stat-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(31, 41, 55, 0.58);
}

.attendance-viewer__stat-value {
  margin-top: 0.35rem;
  font-size: 1.25rem;
  color: #111827;
}

@media (max-width: 960px) {
  .attendance-viewer__summary {
    grid-template-columns: 1fr;
  }

  .attendance-viewer__donut {
    width: 180px;
    height: 180px;
  }
}

@media (max-width: 600px) {
  .attendance-viewer__events {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .attendance-viewer__stats {
    grid-template-columns: 1fr;
  }
}
</style>
